import { fabric } from 'fabric';
import { fxCanvas } from '@frontend/lib/glfx';
import { cloneFabricObject } from '../helpers/clone-handler';
import { getAbsoluteObjectCoords } from '../helpers/get-absolute-object-coords';

export class SkewEffect {
  isActive(obj) {
    const skew = obj.smartObject?.skew || obj.skew;
    return (
      skew &&
      Array.isArray(skew.value) &&
      skew.value.length === 8 &&
      !skew.value.every(v => v === 0) &&
      skew.render
    );
  }

  apply(obj, effectsRenderBuffer) {
    const ref = obj.smartObject || obj;

    const skew = ref.smartObject?.skew || ref.skew;

    obj.setCoords();

    const elementAbsoluteCoords = getAbsoluteObjectCoords(obj);
    const elementWidth = ref.getScaledWidth();
    const elementHeight = ref.getScaledHeight();

    const originalBoxCoords = [
      0, 0,
      elementWidth, 0,
      elementWidth, elementHeight,
      0, elementHeight
    ];

    const modifiedCoords = skew.value.map((v, i) => originalBoxCoords[i] + v);

    const xCoords = [modifiedCoords[0], modifiedCoords[2], modifiedCoords[4], modifiedCoords[6]];
    const yCoords = [modifiedCoords[1], modifiedCoords[3], modifiedCoords[5], modifiedCoords[7]];

    const minX = Math.min(0, ...xCoords);
    const maxX = Math.max(elementWidth, ...xCoords);
    const minY = Math.min(0, ...yCoords);
    const maxY = Math.max(elementHeight, ...yCoords);

    const offsetX = minX < 0 ? -minX : 0;
    const offsetY = minY < 0 ? -minY : 0;

    const canvasWidth = Math.ceil(maxX - minX);
    const canvasHeight = Math.ceil(maxY - minY);

    const tempCanvas = new fabric.StaticCanvas(null, {
      width: canvasWidth,
      height: canvasHeight,
      enableRetinaScaling: false,
    });

    const elementData = obj.toObject();
    elementData.skew = null;
    elementData.shadow = null;

    if (obj.smartObject) {
      const smartObjectAbsoluteCoords = getAbsoluteObjectCoords(obj.smartObject);
      elementData.left = elementAbsoluteCoords.left - smartObjectAbsoluteCoords.left + offsetX;
      elementData.top = elementAbsoluteCoords.top - smartObjectAbsoluteCoords.top + offsetY;
    } else {
      elementData.left = offsetX;
      elementData.top = offsetY;
    }

    if (obj.clipPath) {
      const cp = fabric.util.object.clone(obj.clipPath);
      cp.absolutePositioned = true;
      cp.left = (cp.left || 0) - elementAbsoluteCoords.left + elementData.left;
      cp.top = (cp.top || 0) - elementAbsoluteCoords.top + elementData.top;
      elementData.clipPath = cp;
    }

    const clone = effectsRenderBuffer.canvas
      ? new fabric.Image(effectsRenderBuffer.canvas)
      : cloneFabricObject(obj, elementData);
    
    clone.renderMeta = {
      offsetX,
      offsetY,
      smartObject: obj.smartObject,
      originalCanvas: obj.canvas,
    }

    tempCanvas.add(clone);
    tempCanvas.renderAll();

    const texture = fxCanvas.texture(tempCanvas.getElement());

    const originalCoords = [
      offsetX, offsetY,
      offsetX + elementWidth, offsetY,
      offsetX + elementWidth, offsetY + elementHeight,
      offsetX, offsetY + elementHeight
    ];

    const adjustedModifiedCoords = [
      modifiedCoords[0] + offsetX, modifiedCoords[1] + offsetY,
      modifiedCoords[2] + offsetX, modifiedCoords[3] + offsetY,
      modifiedCoords[4] + offsetX, modifiedCoords[5] + offsetY,
      modifiedCoords[6] + offsetX, modifiedCoords[7] + offsetY,
    ];

    obj.wCoords = {
      tl: { x: modifiedCoords[0] + obj.left, y: modifiedCoords[1] + obj.top },
      tr: { x: modifiedCoords[2] + obj.left, y: modifiedCoords[3] + obj.top },
      br: { x: modifiedCoords[4] + obj.left, y: modifiedCoords[5] + obj.top },
      bl: { x: modifiedCoords[6] + obj.left, y: modifiedCoords[7] + obj.top }
    };

    fxCanvas
      .draw(texture)
      .perspective(originalCoords, adjustedModifiedCoords)
      .update();

    texture.destroy();

    const resultCanvas = document.createElement('canvas');
    const resultCanvasCtx = resultCanvas.getContext('2d');

    resultCanvas.width = canvasWidth;
    resultCanvas.height = canvasHeight;

    resultCanvasCtx.drawImage(fxCanvas, 0, 0);
    
    const refAbsoluteCoords = getAbsoluteObjectCoords(ref);
    return {
      canvas: resultCanvas,
      offsetX: offsetX - (refAbsoluteCoords.left - obj.left),
      offsetY: offsetY - (refAbsoluteCoords.top - obj.top)
    }
  }
}

export const initSkewControls = () => {
  const cornerNames = ['tl', 'tr', 'br', 'bl'];

  const getSkewIndex = {
    tl: [0, 1],
    tr: [2, 3],
    br: [4, 5],
    bl: [6, 7],
  };

  cornerNames.forEach(corner => {
    fabric.Object.prototype.controls[corner + '_skew'] = new fabric.Control({
      actionName: 'skew',
      getVisibility: function(fabricObject, controlKey) {
        const objectVisibility = fabricObject._controlsVisibility;
        if (objectVisibility && typeof objectVisibility[controlKey] !== 'undefined') {
          return objectVisibility[controlKey];
        }
        const skew = fabricObject.smartObject?.skew || fabricObject.skew;
        return skew && skew.render && !fabricObject.smartObject;
      },
      positionHandler: function (dim, finalMatrix, fabricObject) {
        const zoom = (fabricObject.wCoords ? fabricObject.canvas.getZoom() : 1);
        const coords = fabricObject.wCoords || fabricObject.oCoords;

        if (!coords) {
          return {x: 0, y: 0}
        }

        const cornerOffsets = {
          tl: [coords.tl.x, coords.tl.y],
          tr: [coords.tr.x, coords.tr.y],
          br: [coords.br.x, coords.br.y],
          bl: [coords.bl.x, coords.bl.y],
        };

        const [dx, dy] = cornerOffsets[corner];

        return {
          x: dx * zoom,
          y: dy * zoom
        };
      },
      actionHandler: function (eventData, transform, x, y) {
        const fabricObject = transform.target;
        const idx = getSkewIndex[corner];
        const last = transform.lastX !== undefined ? [transform.lastX, transform.lastY] : [x, y];
        const dx = x - last[0];
        const dy = y - last[1];
        transform.lastX = x;
        transform.lastY = y;

        fabricObject.skew.value[idx[0]] += dx;
        fabricObject.skew.value[idx[1]] += dy;

        fabricObject.fire('skew:changed', {
          corner,
          dx,
          dy,
          skew: fabricObject.skew?.value,
          pointer: { x, y }
        });

        fabricObject.canvas.requestRenderAll();
        return true;
      },
      render: function (ctx, left, top, styleOverride, fabricObject) {
        const zoom = (fabricObject.wCoords ? fabricObject.canvas.getZoom() : 1);

        ctx.save();
        ctx.fillStyle = 'red';
        ctx.beginPath();
        ctx.arc(left, top, fabricObject.cornerSize / 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();

        const coords = fabricObject.wCoords || fabricObject.oCoords;
        const center = {
          x: (coords.tl.x + coords.tr.x + coords.br.x + coords.bl.x) / 4 * zoom,
          y: (coords.tl.y + coords.tr.y + coords.br.y + coords.bl.y) / 4 * zoom
        };

        ctx.save();
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth = 1 / zoom;
        ctx.beginPath();
        ctx.moveTo(center.x, center.y);
        ctx.lineTo(left, top);
        ctx.stroke();
        ctx.restore();
      },
      cursorStyleHandler: function () {
        return 'pointer';
      },
    });
  });
}
